import { Injectable, InjectionToken, inject } from "@angular/core";

import { InfraMap, InfraSettings } from "@hermes/core-env-infra";

import { Context } from "./context";

/**
 * This allow us to inject the current
 */
export const INFRA_SETTINGS: InjectionToken<InfraSettings> =
    new InjectionToken<InfraSettings>("ENV_SETTINGS");

/**
 * Injection token for all the env-infra config.
 */
export const INFRA_MAP: InjectionToken<InfraMap> = new InjectionToken<InfraMap>(
    "ENV_INFRA",
);

/**
 * InfraSettings Factory, extract value from EnvInfra map.
 * Values are extracted from the InfraMap depending to host.
 */
export function infraSettingsFactory(
    context: Context,
    environmentInfra: InfraMap,
): InfraSettings {
    const host = context.getCurrentHost();

    if (environmentInfra[host]) {
        return environmentInfra[host];
    }
    throw new Error(
        `No configuration found in environment vars for the host <${host}>`,
    );
}

/**
 * This class is used to declare some global constants that depends on environment.
 */
@Injectable({ providedIn: "root" })
export class Settings {
    private readonly settings = inject<InfraSettings>(INFRA_SETTINGS);
    private readonly context = inject(Context);

    /**
     * Url for the content-manager api.
     */
    public get apiUrl(): string {
        if (this.context.isInServerMode()) {
            return this.settings.backendSSR ?? "";
        }

        return this.settings.backendCSR;
    }

    /**
     * @returns The internal Url of the api gateway
     */
    public get hcomGatewaySSR(): string {
        return this.settings.hcomGatewaySSR;
    }

    /**
     * Public URL of the backend API
     */
    public get apiPublicUrl(): string {
        return this.settings.backendCSR;
    }

    /**
     * Environment (ppd, prd, ts3, int1, etc...)
     */
    public get envKey() {
        return this.settings.environment;
    }

    /**
     * Base url for Edito assets.
     */
    public get assetsUrlEditoContent(): string {
        return `${this.settings.assets}/is/content/hermesedito/`;
    }

    /**
     * Base url for Edito images assets.
     */
    public get assetsUrlEditoImage(): string {
        return `${this.settings.assets}/is/image/hermesedito/`;
    }

    /**
     * Base url for product assets.
     */
    public get assetsUrlProduct(): string {
        return `${this.settings.assets}/is/image/hermesproduct/`;
    }

    /**
     * Base url for product assets on sandbox.
     */
    public get assetsUrlProductSandbox(): string {
        return `${this.settings.assets}/is/image/hermessandbox/`;
    }

    public get externalTrustedHosts(): string[] {
        return this.settings.externalTrustedHosts;
    }

    /**
     * Faro configuration
     */
    public get faroConfiguration() {
        return this.settings.faroConfiguration;
    }

    /**
     * Sensors data configuration (GTM for china)
     */
    public get sensorsConfiguration() {
        return this.settings.sensorsdata;
    }
}
